import * as React from 'react';
import memoizeOne from 'memoize-one';
import {
  calculateHitMaps,
  DataRow,
  DataTable,
  HitMaps,
  MetricBlue,
  MetricComparisonColor,
  MetricsGrey,
  throwIfUndefined,
  ValueAccessor,
} from '@tableau/metrics-engine';
import { HtmlChiaSeed } from './HtmlChiaSeed';

export interface TimeSeriesInteractiveLayerProps {
  readonly size: {
    height: number;
    width: number;
  };
  readonly data: DataTable;
  readonly tooltipFormatter: (date: Date) => string;
  readonly domain: { x: [Date, Date]; y: [number, number] };
  readonly padding: { top: number; left: number; right: number; bottom: number };
  readonly onDataPointActivated: (point: DataRow) => void;
  readonly measureAccessor: (point: DataRow) => number;
  readonly timestampAccessor?: (point: DataRow) => Date | undefined;
  readonly comparisonAccessor?: ValueAccessor<number | undefined>;
  readonly shouldRenderComparisonDot?: boolean;
  readonly colorIndicatorsEnabled: boolean;
  readonly colorAccessor: ValueAccessor<string>;
}

interface TimeSeriesInteractiveLayerState {
  readonly dataPointIndex: number;
  readonly tooltipText?: string;
}

/**
 * The `TimeSeriesInteractiveLayer` is responsible for rendering the elements of
 * the `DetailTimeSeries` that respond to user interaction, as well as tracking
 * pointer interactions with the time series.
 *
 * Specifically:
 * - The "Indicator Dot", also known as the {@link HtmlChiaSeed}, that is
 * displayed for the selected mark on the primary and comparison lines.
 * - The "Indicator Bar", which highlights the currently selected date/time
 * - The tooltip, which is displayed above the indicator bar while the user is
 * hovering over the timeseries.
 *
 * The key constraint for this class is that rendering remain _very fast_. We
 * want user interaction with the timeseries to feel instantanous, regardless of
 * how large the data table being rendered is.  As such, we need to limit the
 * amount of logic or computation that occurs as part of each render loop.
 * Rendering the entire `DetailTimeSeries` is too slow to do on mouse
 * interaction, so these interactive elements are kept seperate from the rest of
 * the time series chart.
 *
 * Currently, this class is an "uncontrolled component": it is responsible for
 * keeping track of the "selected data point", rather than being told which data
 * point is selected via the props interface. This means it is possible for the
 * BAN on the `MetricDetailView` to get out of sync with the selected data point
 * here. This is a potential hazard that we should remove, see [Story 1387382:
 * Use single source of truth for selected point in detail
 * screen](https://tfs.tsi.lan/tfs/DefaultCollection/Tableau-Dev/_workitems/edit/1387382)
 *
 * The current implementation tracks the selected mark in state via the
 * {@link TimeSeriesInteractiveLayerState.dataPointIndex}, and translates back and
 * forth between the index and the x/y coordinates of the selected point via
 * pre-calculated {@link HitMaps}.
 */
export class TimeSeriesInteractiveLayer extends React.PureComponent<
  TimeSeriesInteractiveLayerProps,
  TimeSeriesInteractiveLayerState
> {
  private hitMaps: HitMaps;
  private scrubInProgress: boolean = false;

  public constructor(props: TimeSeriesInteractiveLayerProps) {
    super(props);
    this.hitMaps = this.generateHitMaps();
    this.state = { dataPointIndex: this.props.data.length - 1 };
  }

  public render(): JSX.Element {
    const actualArea = {
      top: this.props.padding.top,
      left: this.props.padding.left,
      height: this.props.size.height - (this.props.padding.top + this.props.padding.bottom),
      width: this.props.size.width - this.props.padding.left,
    };

    this.hitMaps = this.generateHitMaps();
    const dataPointIndex =
      this.state.dataPointIndex < this.hitMaps.mapOfDataPointIndexToCoords.length
        ? this.state.dataPointIndex
        : this.hitMaps.mapOfDataPointIndexToCoords.length - 1;

    const { x, y } = this.hitMaps.mapOfDataPointIndexToCoords[dataPointIndex];

    const dataRow = this.props.data[dataPointIndex];
    const comparisonMeasure = dataRow && this.props.comparisonAccessor && this.props.comparisonAccessor(dataRow);
    const comparisonY = comparisonMeasure && this.hitMaps.measureScale(comparisonMeasure);

    const statusColor = dataRow && this.props.colorAccessor(dataRow);

    const indicatorLineColor = this.props.colorIndicatorsEnabled ? MetricsGrey : MetricBlue;

    const dotColor = this.props.colorIndicatorsEnabled ? statusColor ?? MetricsGrey : MetricBlue;
    return (
      <div
        data-tb-test-id="time-series-interactive-layer"
        style={{
          position: 'absolute',
          ...actualArea,
        }}
        onMouseMove={this.handleMouseMove}
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
        onTouchStart={this.handleMouseEnter}
        onTouchEnd={this.handleMouseLeave}
        onTouchMove={this.handleTouchMove}
      >
        <div
          style={{
            position: 'relative',
            height: actualArea.height + TOOLTIP_OFFSET,
            top: -TOOLTIP_OFFSET,
            transform: `translateX(${x - 1}px)`,
            ...indicatorLineStyle(indicatorLineColor),
          }}
        />
        <div
          style={{
            position: 'relative',
            transform: `translateX(${x}px)`,
            top: -actualArea.top,
            ...tooltipStyle,
            color: indicatorLineColor,
          }}
        >
          {this.state.tooltipText}
        </div>
        {this.props.shouldRenderComparisonDot && comparisonY !== undefined && (
          <HtmlChiaSeed
            x={x}
            y={comparisonY}
            color={MetricComparisonColor}
            colorIndicatorsEnabled={this.props.colorIndicatorsEnabled}
            haloRatio={0}
          />
        )}
        {y !== undefined && (
          <HtmlChiaSeed
            x={x}
            y={y}
            color={dotColor}
            colorIndicatorsEnabled={this.props.colorIndicatorsEnabled}
            haloRatio={2.5}
          />
        )}
      </div>
    );
  }

  private handleMouseEnter = () => {
    this.scrubInProgress = true;
  };

  private handleMouseLeave = () => {
    this.scrubInProgress = false;
    this.setState({ tooltipText: undefined });
  };

  private handleMouseMove = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    this.scrubInProgress = true;
    this.updateForPointerLocation(e.currentTarget, e.clientX);
  };

  private handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    this.updateForPointerLocation(e.currentTarget, e.touches.item(0).clientX);
  };

  private updateForPointerLocation = (target: Element, clientX: number) => {
    const rect = target.getBoundingClientRect();
    const xOffset = rect.left;
    const x = Math.round(clientX - xOffset);
    const dataPointIndex = this.hitMaps.mapOfPixelXCoordsToIndexInDataPoints[x];
    this.updateDotAndLineLocationForDataPointIndex(dataPointIndex);
  };

  private updateDotAndLineLocationForDataPointIndex = (dataPointIndex: number): void => {
    const dataPoint = this.props.data[dataPointIndex];
    if (dataPoint) {
      this.props.onDataPointActivated(dataPoint);
      const timestamp = this.props.timestampAccessor && this.props.timestampAccessor(dataPoint);
      const tooltipText = this.scrubInProgress && timestamp ? this.props.tooltipFormatter(timestamp) : undefined;
      this.setState({ dataPointIndex, tooltipText });
    }
  };

  /**
   * This takes the size of the space the container View occupies,
   * then subtracts padding giving the data area size.  Then it builds the two
   * hit maps used during gesture handling.
   */
  private generateHitMaps = (): HitMaps => {
    return this.generateHitMapsMemoized(this.props.size, this.props.padding, this.props.domain, this.props.data);
  };

  // tslint:disable-next-line: typedef
  private generateHitMapsMemoized = memoizeOne(
    (
      size: TimeSeriesInteractiveLayerProps['size'],
      padding: TimeSeriesInteractiveLayerProps['padding'],
      domain: TimeSeriesInteractiveLayerProps['domain'],
      data: DataTable
    ): HitMaps => {
      const finalWidth = size.width - (padding.left + padding.right);
      const finalHeight = size.height - (padding.top + padding.bottom);

      return calculateHitMaps(
        data,
        finalWidth,
        finalHeight,
        domain.x as [Date, Date],
        domain.y as [number, number],
        padding.right,
        this.props.measureAccessor,
        this.props.timestampAccessor && throwIfUndefined(this.props.timestampAccessor)
      );
    }
  );
}

const VERTICAL_BAR_TOOLTIP_CONTAINER_WIDTH = 160;

// We set both the background color and the left border style / color to support browser zoom.
// Background of 1px will allow the width to grow when the user zooms in.
// Left border width of 'thin' will allow the line to remain visible when the user zooms out.
const indicatorLineStyle = (color: string): React.CSSProperties => ({
  width: 1,
  borderLeftStyle: 'solid',
  borderLeftWidth: 'thin',
  borderLeftColor: color,
  backgroundColor: color,
  pointerEvents: 'none',
});

const TOOLTIP_OFFSET = 20;

const tooltipStyle: React.CSSProperties = {
  position: 'absolute',
  left: -VERTICAL_BAR_TOOLTIP_CONTAINER_WIDTH / 2,
  width: VERTICAL_BAR_TOOLTIP_CONTAINER_WIDTH,
  marginTop: 10,
  textAlign: 'center',
  fontSize: 14,
  fontFamily: 'Benton Sans',
};
